import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { PlatformDetail } from "@codestack/shared";
import { updatePlatformSchema } from "@codestack/shared";
import { Badge, Button, Input, Modal, Select, StatusBadge, Textarea } from "@codestack/ui";
import { apiRequest, ApiError, downloadFile } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { ErrorState, InlineAlert, LoadingState } from "../components/States";
import { dateTime, money, percent, relativeTime } from "../lib/format";

type RotatedCredentials = { keyPrefix: string; secret: string };

type SelfTestCheck = {
  id: string;
  label: string;
  status: "ok" | "warn" | "fail";
  detail: string;
};

type SelfTestResult = {
  ok: boolean;
  summary: string;
  status: number | null;
  latencyMs: number | null;
  checks: SelfTestCheck[];
  testedAt: string;
};

const CHECK_TONE: Record<SelfTestCheck["status"], "success" | "warning" | "danger"> = {
  ok: "success",
  warn: "warning",
  fail: "danger",
};

type EditState = {
  name: string;
  description: string;
  domain: string;
  callbackUrl: string;
  status: "ACTIVE" | "SUSPENDED" | "ARCHIVED";
  showOnHomepage: boolean;
  feePercent: string;
};

export function PlatformDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<PlatformDetail>(
    id ? `/admin/platforms/${id}` : null,
  );

  const [edit, setEdit] = useState<EditState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotated, setRotated] = useState<RotatedCredentials | null>(null);

  const [settingsDraft, setSettingsDraft] = useState<Array<{ key: string; value: string }>>([]);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);

  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SelfTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canWrite = can("platform:write");

  /** Exports every transaction for this platform, for accounting. */
  const runExport = async (format: "csv" | "pdf") => {
    if (!id) return;
    setExporting(format);
    setExportError(null);
    try {
      await downloadFile(
        "/admin/transactions/export",
        { format, platformId: id },
        `codestack-transactions-${data?.slug ?? id}.${format}`,
      );
    } catch (cause) {
      setExportError(
        cause instanceof Error ? cause.message : "The export could not be generated.",
      );
    } finally {
      setExporting(null);
    }
  };

  const runSelfTest = async () => {
    if (!id) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await apiRequest<SelfTestResult>(`/admin/platforms/${id}/test`, {
        method: "POST",
      });
      setTestResult(result);
    } catch (cause) {
      setTestError(cause instanceof ApiError ? cause.message : "The self-test could not be run.");
    } finally {
      setTesting(false);
    }
  };

  const revealSecret = async (credentialId: string) => {
    if (!id) return;
    setRevealing(credentialId);
    setRevealError(null);
    try {
      const result = await apiRequest<{ keyPrefix: string; secret: string }>(
        `/admin/platforms/${id}/credentials/${credentialId}/secret`,
      );
      setRevealed((current) => ({ ...current, [credentialId]: result.secret }));
    } catch (cause) {
      setRevealError(
        cause instanceof ApiError ? cause.message : "The secret could not be revealed.",
      );
    } finally {
      setRevealing(null);
    }
  };

  const hideSecret = (credentialId: string) => {
    setRevealed((current) => {
      const next = { ...current };
      delete next[credentialId];
      return next;
    });
  };

  const copySecret = async (credentialId: string, secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(credentialId);
      setTimeout(() => setCopied((current) => (current === credentialId ? null : current)), 2000);
    } catch {
      setRevealError("Could not copy to the clipboard — select the secret and copy it manually.");
    }
  };

  useEffect(() => {
    if (!data) return;
    setEdit({
      name: data.name,
      description: data.description ?? "",
      domain: data.domain ?? "",
      callbackUrl: data.callbackUrl ?? "",
      status: data.status,
      showOnHomepage: data.showOnHomepage,
      feePercent: String(data.feePercent),
    });
    setSettingsDraft(data.settings.length > 0 ? data.settings : [{ key: "", value: "" }]);
  }, [data]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!edit || !id) return;
    setSaveError(null);
    setFieldErrors({});
    setSaved(false);

    const parsed = updatePlatformSchema.safeParse({
      name: edit.name.trim(),
      description: edit.description.trim(),
      domain: edit.domain.trim(),
      callbackUrl: edit.callbackUrl.trim(),
      status: edit.status,
      showOnHomepage: edit.showOnHomepage,
      feePercent: Number(edit.feePercent),
    });

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    try {
      await apiRequest(`/admin/platforms/${id}`, { method: "PATCH", body: parsed.data });
      setSaved(true);
      reload();
    } catch (cause) {
      setSaveError(
        cause instanceof ApiError ? cause.message : "The platform could not be updated.",
      );
    } finally {
      setSaving(false);
    }
  };

  const rotate = async () => {
    if (!id) return;
    setRotating(true);
    setRotateError(null);
    try {
      const result = await apiRequest<RotatedCredentials>(
        `/admin/platforms/${id}/credentials/rotate`,
        { method: "POST" },
      );
      setRotated(result);
      reload();
    } catch (cause) {
      setRotateError(
        cause instanceof ApiError ? cause.message : "Credentials could not be rotated.",
      );
    } finally {
      setRotating(false);
    }
  };

  const closeRotate = () => {
    setRotateOpen(false);
    setRotated(null);
    setRotateError(null);
  };

  const openDelete = () => {
    setDeleteConfirm("");
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteConfirm("");
    setDeleteError(null);
  };

  const remove = async () => {
    if (!id || !data) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiRequest<void>(`/admin/platforms/${id}`, {
        method: "DELETE",
        body: { confirmSlug: deleteConfirm.trim() },
      });
      navigate("/platforms", { replace: true });
    } catch (cause) {
      setDeleteError(
        cause instanceof ApiError ? cause.message : "The platform could not be deleted.",
      );
      setDeleting(false);
    }
  };

  const saveSettings = async () => {
    if (!id) return;
    const cleaned = settingsDraft.filter((entry) => entry.key.trim() !== "");
    setSettingsSaving(true);
    setSettingsMessage(null);
    try {
      await apiRequest(`/admin/platforms/${id}/settings`, {
        method: "PUT",
        body: { settings: cleaned.map((entry) => ({ key: entry.key.trim(), value: entry.value })) },
      });
      setSettingsMessage("Platform settings saved.");
      reload();
    } catch (cause) {
      setSettingsMessage(
        cause instanceof ApiError ? cause.message : "Settings could not be saved.",
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  if (loading && !data) return <LoadingState label="Loading platform…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link to="/platforms" className="text-xs text-ink-500 hover:text-ink-300">
            ← Back to platforms
          </Link>
          <div className="mt-2 flex items-center gap-3">
            <h1 className="font-display text-xl font-bold text-white">{data.name}</h1>
            <StatusBadge status={data.status} />
          </div>
          <p className="mt-1 font-mono text-[11px] text-ink-500">{data.slug}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting !== null}
            onClick={() => void runExport("csv")}
          >
            {exporting === "csv" ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting !== null}
            onClick={() => void runExport("pdf")}
          >
            {exporting === "pdf" ? "Exporting…" : "Export PDF"}
          </Button>
          {canWrite ? (
            <Button variant="secondary" onClick={() => setRotateOpen(true)}>
              Rotate credentials
            </Button>
          ) : null}
          {canWrite ? (
            <Button variant="danger" onClick={openDelete}>
              Delete platform
            </Button>
          ) : null}
        </div>
      </div>

      {exportError ? (
        <div className="admin-card border border-red-500/30 p-3 text-xs text-red-300">
          {exportError}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="admin-card p-5">
          <p className="admin-label">Lifetime volume</p>
          <p className="admin-value mt-2">{money(data.stats?.totalVolumeMinor ?? "0", "NGN")}</p>
        </div>
        <div className="admin-card p-5">
          <p className="admin-label">Transactions</p>
          <p className="admin-value mt-2">{data.stats?.transactionCount ?? 0}</p>
        </div>
        <div className="admin-card p-5">
          <p className="admin-label">Success rate</p>
          <p className="admin-value mt-2">{percent(data.stats?.successRate ?? 0, 0)}</p>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.2fr_1fr]">
        <section className="admin-card p-5">
          <h2 className="font-display text-sm font-semibold text-white">Configuration</h2>
          <p className="mt-1 text-xs text-ink-500">
            Changes are audited and take effect immediately.
          </p>

          {edit ? (
            <form onSubmit={save} className="mt-5 space-y-4">
              {saveError ? <InlineAlert tone="danger">{saveError}</InlineAlert> : null}
              {saved ? <InlineAlert tone="success">Platform updated.</InlineAlert> : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Name"
                  value={edit.name}
                  error={fieldErrors.name}
                  onChange={(event) => setEdit({ ...edit, name: event.target.value })}
                  disabled={!canWrite}
                />
                <Input
                  label="Fee percent"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={edit.feePercent}
                  error={fieldErrors.feePercent}
                  onChange={(event) => setEdit({ ...edit, feePercent: event.target.value })}
                  disabled={!canWrite}
                />
              </div>

              <Textarea
                label="Description"
                rows={3}
                value={edit.description}
                error={fieldErrors.description}
                onChange={(event) => setEdit({ ...edit, description: event.target.value })}
                disabled={!canWrite}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Domain"
                  value={edit.domain}
                  error={fieldErrors.domain}
                  onChange={(event) => setEdit({ ...edit, domain: event.target.value })}
                  placeholder="numbaport.com"
                  hint="Bare domain is fine — no https:// needed."
                  disabled={!canWrite}
                />
                <Input
                  label="Callback URL"
                  value={edit.callbackUrl}
                  error={fieldErrors.callbackUrl}
                  onChange={(event) => setEdit({ ...edit, callbackUrl: event.target.value })}
                  disabled={!canWrite}
                />
              </div>

              <Select
                label="Status"
                value={edit.status}
                onChange={(event) =>
                  setEdit({ ...edit, status: event.target.value as EditState["status"] })
                }
                disabled={!canWrite}
              >
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="ARCHIVED">Archived</option>
              </Select>

              <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-ink-950 accent-brand-500"
                    checked={edit.showOnHomepage}
                    onChange={(event) =>
                      setEdit({ ...edit, showOnHomepage: event.target.checked })
                    }
                    disabled={!canWrite}
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-ink-200">
                      Show on homepage
                    </span>
                    <span className="mt-0.5 block text-[11px] text-ink-500">
                      Lists this platform in the public directory at{" "}
                      <span className="font-mono">codestacktechnologies.com</span>. Turning it
                      off only hides it from the website — API access and payments are
                      unaffected.
                    </span>
                  </span>
                </label>
              </div>

              {canWrite ? (
                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              ) : null}
            </form>
          ) : null}
        </section>

        <div className="space-y-5">
          <section className="admin-card p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="font-display text-sm font-semibold text-white">API credentials</h2>
                <p className="mt-1 text-xs text-ink-500">
                  Secrets are encrypted at rest. Reveal one to copy it into the platform's
                  configuration — every reveal is recorded in the audit log.
                </p>
              </div>
              {canWrite ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={testing}
                  onClick={runSelfTest}
                  className="shrink-0"
                >
                  {testing ? "Testing…" : "Test API"}
                </Button>
              ) : null}
            </div>

            {testError ? (
              <div className="mt-3">
                <InlineAlert tone="danger">{testError}</InlineAlert>
              </div>
            ) : null}

            {testResult ? (
              <div className="mt-4 rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-white">
                    {testResult.ok ? "Self-test passed" : "Self-test failed"}
                  </p>
                  <span className="text-[11px] text-ink-500">
                    {dateTime(testResult.testedAt)}
                    {testResult.latencyMs !== null ? ` · ${testResult.latencyMs} ms` : ""}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ink-400">{testResult.summary}</p>
                <ul className="mt-3 space-y-2">
                  {testResult.checks.map((check) => (
                    <li key={check.id} className="flex items-start gap-2.5">
                      <Badge tone={CHECK_TONE[check.status]} className="mt-0.5 shrink-0">
                        {check.status === "ok" ? "OK" : check.status === "warn" ? "Warn" : "Fail"}
                      </Badge>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-ink-200">{check.label}</p>
                        <p className="break-words text-[11px] text-ink-500">{check.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-[11px] text-ink-500">
                  Signs real requests with this platform's own credential and walks the full
                  flow — authenticate, create an order, read it back, then initialise a payment
                  through CodeStack's API to Squad. The test payment is cancelled afterwards.
                </p>
              </div>
            ) : null}

            <ul className="mt-4 space-y-3">
              {data.credentials.map((credential) => (
                <li
                  key={credential.id}
                  className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <code className="font-mono text-xs text-brand-200">
                      {credential.keyPrefix}
                    </code>
                    {credential.revokedAt ? (
                      <Badge tone="danger">Revoked</Badge>
                    ) : (
                      <Badge tone="success">Active</Badge>
                    )}
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-ink-500">
                    <div>
                      <dt>Rotated</dt>
                      <dd className="text-ink-300">{dateTime(credential.rotatedAt)}</dd>
                    </div>
                    <div>
                      <dt>Last used</dt>
                      <dd className="text-ink-300">
                        {credential.lastUsedAt ? relativeTime(credential.lastUsedAt) : "Never"}
                      </dd>
                    </div>
                  </dl>

                  {canWrite && !credential.revokedAt ? (
                    <div className="mt-3 border-t border-white/[0.06] pt-3">
                      {revealed[credential.id] ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <code className="min-w-0 flex-1 break-all rounded bg-ink-950 px-2 py-1.5 font-mono text-[11px] text-emerald-300">
                              {revealed[credential.id]}
                            </code>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => copySecret(credential.id, revealed[credential.id]!)}
                            >
                              {copied === credential.id ? "Copied" : "Copy"}
                            </Button>
                          </div>
                          <button
                            type="button"
                            className="text-[11px] text-ink-500 underline hover:text-ink-300"
                            onClick={() => hideSecret(credential.id)}
                          >
                            Hide secret
                          </button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={revealing === credential.id}
                          onClick={() => revealSecret(credential.id)}
                        >
                          {revealing === credential.id ? "Revealing…" : "Reveal secret"}
                        </Button>
                      )}
                      {revealError ? (
                        <p className="mt-2 text-[11px] text-red-400">{revealError}</p>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
              {data.credentials.length === 0 ? (
                <li className="text-xs text-ink-500">No credentials issued yet.</li>
              ) : null}
            </ul>
          </section>

          <section className="admin-card p-5">
            <h2 className="font-display text-sm font-semibold text-white">Platform settings</h2>
            <p className="mt-1 text-xs text-ink-500">
              Key/value pairs exposed to the platform integration.
            </p>

            <div className="mt-4 space-y-2">
              {settingsDraft.map((entry, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Input
                    placeholder="key"
                    value={entry.key}
                    disabled={!canWrite}
                    onChange={(event) => {
                      const next = [...settingsDraft];
                      next[index] = { ...entry, key: event.target.value };
                      setSettingsDraft(next);
                    }}
                  />
                  <Input
                    placeholder="value"
                    value={entry.value}
                    disabled={!canWrite}
                    onChange={(event) => {
                      const next = [...settingsDraft];
                      next[index] = { ...entry, value: event.target.value };
                      setSettingsDraft(next);
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!canWrite}
                    onClick={() =>
                      setSettingsDraft(settingsDraft.filter((_, i) => i !== index))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>

            {settingsMessage ? (
              <p className="mt-3 text-xs text-ink-400">{settingsMessage}</p>
            ) : null}

            {canWrite ? (
              <div className="mt-4 flex justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSettingsDraft([...settingsDraft, { key: "", value: "" }])}
                >
                  Add setting
                </Button>
                <Button type="button" size="sm" disabled={settingsSaving} onClick={saveSettings}>
                  {settingsSaving ? "Saving…" : "Save settings"}
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <Modal
        open={rotateOpen}
        onClose={closeRotate}
        title={rotated ? "Credentials rotated" : "Rotate API credentials"}
        description={
          rotated
            ? "Copy the new secret now — it will not be shown again."
            : "Existing credentials will be revoked immediately. The platform must be updated before its next request."
        }
        footer={
          rotated ? (
            <Button onClick={closeRotate}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeRotate} disabled={rotating}>
                Cancel
              </Button>
              <Button onClick={rotate} disabled={rotating}>
                {rotating ? "Rotating…" : "Rotate now"}
              </Button>
            </>
          )
        }
      >
        {rotateError ? <InlineAlert tone="danger">{rotateError}</InlineAlert> : null}
        {rotated ? (
          <div className="space-y-3">
            <InlineAlert tone="warning">
              The previous secret stopped working the moment you rotated.
            </InlineAlert>
            <div>
              <p className="admin-label">Key prefix</p>
              <code className="mt-1 block break-all rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-brand-200">
                {rotated.keyPrefix}
              </code>
            </div>
            <div>
              <p className="admin-label">Secret key</p>
              <code className="mt-1 block break-all rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-amber-200">
                {rotated.secret}
              </code>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink-400">
            This revokes all active credentials for{" "}
            <span className="text-ink-200">{data.name}</span> and issues a new key pair. Requests
            signed with the old secret will be rejected with a 401.
          </p>
        )}
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={closeDelete}
        title="Delete platform"
        description="This permanently removes the platform and everything attached to it."
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={remove}
              disabled={deleting || deleteConfirm.trim() !== data.slug}
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {deleteError ? <InlineAlert tone="danger">{deleteError}</InlineAlert> : null}
          <InlineAlert tone="warning">
            Deleting <span className="font-semibold">{data.name}</span> also deletes its API
            credentials, settings, customers, orders, payments, refunds, settlements and ledger
            entries. This cannot be undone.
          </InlineAlert>
          <p className="text-sm text-ink-400">
            {data.stats?.transactionCount
              ? `This platform has ${data.stats.transactionCount} transaction${
                  data.stats.transactionCount === 1 ? "" : "s"
                } on record. Export them first if you need them for accounting.`
              : "This platform has no transactions on record."}
          </p>
          <Input
            label="Confirm the slug to continue"
            value={deleteConfirm}
            onChange={(event) => setDeleteConfirm(event.target.value)}
            placeholder={data.slug}
            hint={`Type ${data.slug} exactly.`}
            autoFocus
            disabled={deleting}
          />
        </div>
      </Modal>
    </div>
  );
}
